import React, { useMemo, useState } from 'react';
import { useVendorInvoices, useLinkedCustomers } from '@api/hooks/useVendorPortal';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { TableViewControls } from '@components/common/TableViewControls';
import { TableViewLayout } from '@components/common/TableViewLayout';
import { TablePagination } from '@components/common/TablePagination';
import { tableHeaderCellClass, tableHeaderRowClass } from '@components/common/tableViewStyles';
import { FileText } from 'lucide-react';
import type { LinkedCustomerResponse, VendorInvoiceResponse } from '@schemas/api';
import { formatDate } from '@utils/formatters';

const STATUS_COLORS: Record<string, string> = {
  Received: 'bg-gray-100 text-gray-700',
  Processing: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-blue-100 text-blue-800',
  Paid: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  'Pending Approval': 'bg-yellow-100 text-yellow-800',
  'Approved': 'bg-blue-100 text-blue-800',
  'Paid': 'bg-green-100 text-green-800',
  'Cancelled': 'bg-red-100 text-red-800',
};

const PAGE_SIZE = 50;
const VENDOR_INVOICE_STATUS_OPTIONS = [
  { id: '', label: 'All Statuses' },
  { id: 'Received', label: 'Received' },
  { id: 'Processing', label: 'Processing' },
  { id: 'Approved', label: 'Approved' },
  { id: 'Rejected', label: 'Rejected' },
];

export default function VendorInvoicesPage() {
  const [skip, setSkip] = useState(0);
  const [customerId, setCustomerId] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'invoice_number' | 'date' | 'due_date' | 'amount' | 'customer' | 'status' | 'payment' | 'currency'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: customers } = useLinkedCustomers();
  const { data, isLoading } = useVendorInvoices(skip, PAGE_SIZE, customerId, statusFilter);

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount);
  };

  const invoices = useMemo(() => [...(data?.items ?? [])].filter((invoice) => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
    if (!normalizedSearch) return true;
    return [invoice.invoice_number, invoice.customer_name, invoice.status, invoice.payment_status, invoice.currency]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedSearch);
  }).sort((left, right) => {
    const valueFor = (invoice: VendorInvoiceResponse) => {
      if (sortBy === 'invoice_number') return invoice.invoice_number;
      if (sortBy === 'amount') return invoice.amount;
      if (sortBy === 'customer') return invoice.customer_name;
      if (sortBy === 'status') return invoice.status;
      if (sortBy === 'payment') return invoice.payment_status ?? '';
      if (sortBy === 'currency') return invoice.currency;
      return new Date((sortBy === 'date' ? invoice.invoice_date : invoice.due_date) ?? 0).getTime();
    };
    const leftValue = valueFor(left);
    const rightValue = valueFor(right);
    const comparison = typeof leftValue === 'string'
      ? leftValue.localeCompare(String(rightValue))
      : Number(leftValue) - Number(rightValue);
    return sortOrder === 'asc' ? comparison : -comparison;
  }), [data?.items, searchTerm, sortBy, sortOrder]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-content-primary dark:text-content-primary-dark">
          Invoices
        </h1>
        <p className="mt-1 text-content-secondary dark:text-content-secondary-dark">
          Processing status of invoices submitted to your customers.
        </p>
      </div>

      {isLoading ? (
        <div className="text-gray-500 p-6">Loading invoices...</div>
      ) : !invoices.length ? (
        <div className="text-center py-12 bg-white dark:bg-surface-base-dark rounded-lg border border-border-default">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No invoices found.</p>
          <p className="text-sm text-gray-500 mt-1">
            Invoices submitted to your customers will appear here once they are processed.
          </p>
        </div>
      ) : (
        <TableViewLayout
          title="Invoice history"
          summary={`Showing ${skip + 1}-${Math.min(skip + PAGE_SIZE, data?.total ?? 0)} of ${data?.total ?? 0}`}
          controls={
            <TableViewControls
              activeFilterCount={Number(Boolean(customerId)) + Number(Boolean(statusFilter))}
              onClearFilters={() => {
                setCustomerId(undefined);
                setStatusFilter(undefined);
                setSkip(0);
              }}
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchLabel="Search invoices"
              searchPlaceholder="Search invoice, customer, or payment status…"
              filterContent={
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Customer</label>
                    <SearchableSelect
                      aria-label="Invoice customer"
                      options={[
                        { id: '', label: 'All Customers' },
                        ...(customers?.items ?? []).map((customer: LinkedCustomerResponse) => ({
                          id: customer.customer_tenant_id,
                          label: customer.customer_name,
                        })),
                      ]}
                      value={customerId ?? ''}
                      onChange={(value) => {
                        setCustomerId(value || undefined);
                        setSkip(0);
                      }}
                      clearable={false}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <SearchableSelect
                      aria-label="Invoice status"
                      options={VENDOR_INVOICE_STATUS_OPTIONS}
                      value={statusFilter ?? ''}
                      onChange={(value) => {
                        setStatusFilter(value || undefined);
                        setSkip(0);
                      }}
                      clearable={false}
                    />
                  </div>
                </div>
              }
              sortContent={
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Sort by</label>
                    <SearchableSelect
                      aria-label="Sort invoices by"
                      options={[
                        { id: 'invoice_number', label: 'Invoice number' },
                        { id: 'date', label: 'Invoice date' },
                        { id: 'due_date', label: 'Due date' },
                        { id: 'amount', label: 'Amount' },
                        { id: 'customer', label: 'Customer' },
                        { id: 'status', label: 'Processing status' },
                        { id: 'payment', label: 'Payment status' },
                        { id: 'currency', label: 'Currency' },
                      ]}
                      value={sortBy}
                      onChange={(value) => setSortBy(value as typeof sortBy)}
                      clearable={false}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Direction</label>
                    <SearchableSelect
                      aria-label="Invoice sort direction"
                      options={[{ id: 'desc', label: 'Descending' }, { id: 'asc', label: 'Ascending' }]}
                      value={sortOrder}
                      onChange={(value) => setSortOrder(value as typeof sortOrder)}
                      clearable={false}
                    />
                  </div>
                </div>
              }
            />
          }
          footer={<TablePagination idPrefix="vendor-invoices" page={Math.floor(skip / PAGE_SIZE) + 1} pageSize={PAGE_SIZE} total={data?.total ?? 0} onPageChange={(page) => setSkip((page - 1) * PAGE_SIZE)} />}
        >
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={tableHeaderRowClass}>
              <tr>
                <th className={`${tableHeaderCellClass} text-left`}>Invoice #</th>
                <th className={`${tableHeaderCellClass} text-left`}>Customer</th>
                <th className={`${tableHeaderCellClass} text-left`}>Date</th>
                <th className={`${tableHeaderCellClass} text-left`}>Due Date</th>
                <th className={`${tableHeaderCellClass} text-right`}>Amount</th>
                <th className={`${tableHeaderCellClass} text-left`}>Status</th>
                <th className={`${tableHeaderCellClass} text-left`}>Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {invoices.map((invoice: VendorInvoiceResponse) => (
                <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 font-mono text-sm text-gray-900 dark:text-white">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                    {invoice.customer_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(invoice.due_date)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white text-right font-medium">
                    {formatAmount(invoice.amount, invoice.currency)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-600'}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {invoice.payment_status ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${PAYMENT_STATUS_COLORS[invoice.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                        {invoice.payment_status}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        </TableViewLayout>
      )}
    </div>
  );
}
